import { captureException } from '@sentry/nextjs';
import { useEffect, useRef } from 'react';

type ExtendedWindow = Window &
  typeof globalThis & {
    Trustpilot: {
      loadFromElement: (element: Element) => void;
    };
  };

const POLLING_INTERVAL = 200;
const MAX_RETRIES = 10;

export const useTrustpilotLoader = ({
  onLoad,
  containerRef,
}: {
  onLoad: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
}) => {
  const loadedRef = useRef(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let retryCount = 0;

    /**
     * We need to poll for the Trustpilot script
     * as the window.Trustpilot object may not be available immediately
     * when the component mounts
     */
    const loadTrustpilotWidget = () => {
      if (loadedRef.current) return;

      if (
        typeof window !== 'undefined' &&
        (window as ExtendedWindow).Trustpilot &&
        containerRef.current
      ) {
        (window as ExtendedWindow).Trustpilot.loadFromElement(
          containerRef.current
        );
        loadedRef.current = true;
        onLoad?.();
      } else if (retryCount < MAX_RETRIES) {
        retryCount++;
        timeoutId = setTimeout(loadTrustpilotWidget, POLLING_INTERVAL);
      } else {
        /**
         * We can fail gracefully since without the widget
         * the UI will show a fallback Trustpilot link
         */

        captureException(new Error('Trustpilot script failed to load'));
      }
    };

    loadTrustpilotWidget();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [onLoad, containerRef]);
};
